import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { SignatorySelect } from "./SignatoriesCard";
import { Loader2, Save, Send, Printer, Paperclip, X, Sparkles, Lock, Download, Library, BookmarkPlus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Letterhead, withSigner } from "./Letterhead";
import { DocEditor } from "./DocEditor";
import { DecisionAIBox } from "./DecisionAIBox";
import { DECISION_TEMPLATES, fillTemplate, toTemplate } from "../lib/decisionTemplates";
import { SaveDecisionTplDialog } from "./SaveDecisionTplDialog";
import { printDecision, decisionPDFBlob } from "../lib/decisionPrint";
import { archiveSentDecision } from "../lib/decisionArchive";
import { SaveToPrivateDialog } from "./SaveToPrivateDialog";
import { personMatch } from "../lib/searchText";

const TYPES = ["تعيين", "مهام وظيفية", "نقل", "ترقية", "إنذار", "إنذار أول", "إنذار ثانٍ", "إنذار نهائي", "لفت نظر", "خصم", "مكافأة", "إجازة", "إنهاء خدمة", "تكليف", "بلاغ تغيب", "استقالة", "قبول استقالة", "أخرى"];
const today = () => new Date().toISOString().slice(0, 10);

const EmpPicker = ({ employees, value, onChange }) => {
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const s = q.trim();
    return (employees || []).filter((e) => personMatch(e, s));
  }, [employees, q]);
  useEffect(() => { if (q.trim() && list.length === 1 && list[0].id !== value) onChange(list[0].id); }, [list]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-1.5">
      <Input data-testid="decision-emp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم أو رقم الإقامة…" className="h-9" />
      <select data-testid="decision-emp-select" value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
        <option value="">— بدون موظف —</option>
        {list.map((e) => <option key={e.id} value={e.id}>{e.name}{e.branch ? ` · ${e.branch}` : ""}</option>)}
      </select>
    </div>
  );
};

export const DecisionEditor = ({ open, onOpenChange, decision, employees, onSaved }) => {
  const { company, user } = useAuth();
  const [signer, setSigner] = useState({});
  const sc = withSigner(company, signer);
  const [privOpen, setPrivOpen] = useState(false);
  const ed = useRef(null);
  const fileIn = useRef(null);
  const [f, setF] = useState(() => ({
    type: decision?.type || "أخرى", title: decision?.title || "", date: decision?.date || today(),
    employee_id: decision?.employee_id || "", attachment_id: decision?.attachment_id || null,
    attachment_name: decision?.attachment_name || null, require_signature: decision?.require_signature ?? false, signatory_id: decision?.signatory_id || "",
    ...(decision?.leave_id ? { leave_id: decision.leave_id } : {}),
    ...(decision?.resign_request_id && !decision?.id ? { resign_request_id: decision.resign_request_id, resign_last_day: decision.resign_last_day } : {}),
  }));
  const [busy, setBusy] = useState("");
  const [myTpls, setMyTpls] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);
  const [editTpl, setEditTpl] = useState(null);
  useEffect(() => { if (open) api.decisionTemplates().then(setMyTpls).catch(() => {}); }, [open]);
  const emp = (employees || []).find((e) => e.id === f.employee_id);
  const locked = decision?.status === "signed";

  const srcRef = useRef(null);
  const filledRef = useRef(null);
  const fillInto = (src, e) => {
    const html = fillTemplate(src, e, { company: company?.name || "الشركة", date: f.date });
    srcRef.current = src; filledRef.current = html;
    ed.current?.setHTML(html);
  };
  useEffect(() => {
    // Re-fill the last template/AI text when the employee changes and the text wasn't edited yet
    if (srcRef.current && ed.current?.getHTML() === filledRef.current) fillInto(srcRef.current, emp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.employee_id]);

  const applyTpl = (tp) => {
    fillInto(tp.body ?? tp.body_html ?? "", emp);
    setF((p) => ({ ...p, type: tp.type, title: tp.title }));
    toast.success(`تمت تعبئة «${tp.title}»`);
  };
  const saveTpl = async (title, existing) => {
    const body = { type: f.type, title, body_html: toTemplate(ed.current?.getHTML() || "", emp, { date: f.date, company: company?.name }) };
    try {
      const r = existing ? await api.updateDecisionTemplate(existing.id, body) : await api.createDecisionTemplate(body);
      setMyTpls((p) => existing ? p.map((t) => (t.id === r.id ? r : t)) : [...p, r]);
      setEditTpl(null);
      toast.success(existing ? "تم تحديث النموذج" : "تم حفظ النموذج ضمن القرارات المقترحة ✅");
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر حفظ النموذج"); throw e; }
  };
  const delTpl = async (t) => {
    if (!window.confirm(`حذف النموذج «${t.title}»؟`)) return;
    try { await api.deleteDecisionTemplate(t.id); setMyTpls((p) => p.filter((x) => x.id !== t.id)); if (editTpl?.id === t.id) setEditTpl(null); toast.success("تم حذف النموذج"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
  };
  const upload = async (file) => {
    if (!file) return;
    setBusy("upload");
    try { const r = await api.uploadFile(file); setF((p) => ({ ...p, attachment_id: r.attachment_id, attachment_name: r.attachment_name })); }
    catch (e) { toast.error(e?.response?.data?.detail || "فشل رفع المرفق"); }
    finally { setBusy(""); }
  };
  const save = async (send) => {
    if (!f.title.trim()) { toast.error("اكتب عنوان القرار"); return; }
    if (send && !f.employee_id) { toast.error("اختر الموظف لإرسال القرار"); return; }
    setBusy(send ? "send" : "save");
    try {
      const body = { ...f, employee_id: f.employee_id || null, body_html: ed.current?.getHTML() || "", send };
      const r = decision?.id ? await api.updateDecision(decision.id, body) : await api.createDecision(body);
      if (send) {
        try {
          await archiveSentDecision({ ...docData(), ...r, employee_name: emp?.name || r?.employee_name }, company, true);
          toast.success("تم إرسال القرار للموظف وحُفظ في مكتبة الشركة وملف الموظف ✅");
        } catch { toast.warning("أُرسل القرار للموظف، لكن تعذّر حفظ نسخته في المكتبة — استخدم زر «حفظ في مكتبة الشركة»"); }
      } else toast.success("تم حفظ القرار");
      onSaved?.(r);
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setBusy(""); }
  };
  const docData = () => ({ ...decision, ...f, employee_name: emp?.name || decision?.employee_name, body_html: ed.current?.getHTML() || "" });
  const docName = `${/^قرار/.test(f.title || "") ? "" : "قرار "}${f.title || ""}${emp?.name ? ` - ${emp.name}` : ""}`.trim();
  const pdfName = `${(f.title || "قرار").slice(0, 40)}${emp?.name ? ` - ${emp.name}` : ""}.pdf`;
  const download = async () => {
    setBusy("pdf");
    try { const b = await decisionPDFBlob(docData(), sc, fileUrl); const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = pdfName; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 1000); }
    catch { toast.error("تعذّر إنشاء PDF"); } finally { setBusy(""); }
  };
  const toLibrary = async () => {
    setBusy("lib");
    try {
      const b = await decisionPDFBlob(docData(), sc, fileUrl);
      const up = await api.uploadFile(new File([b], pdfName, { type: "application/pdf" }));
      const r = await api.archiveDecision({ file_id: up.attachment_id, file_name: pdfName, name: docName, employee_id: f.employee_id || null });
      toast.success(r.copied_to_employee ? "تم الحفظ في مكتبة الشركة ونُسخ لملف الموظف ✅" : "تم الحفظ في مكتبة الشركة ✅ (لم يُحدد موظف)");
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(""); }
  };
  const print = () => printDecision({ ...decision, ...f, employee_name: emp?.name || decision?.employee_name, body_html: ed.current?.getHTML() || "" }, company, fileUrl);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[96vw] max-h-[94vh] overflow-y-auto p-4 sm:p-6" dir="rtl" data-testid="decision-editor-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading">{decision?.id ? `قرار ${decision.number}` : "قرار إداري جديد"}</DialogTitle>
          <DialogDescription>اختر قالباً جاهزاً أو اكتب القرار بحرية على ترويسة الشركة</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
          <aside className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-500">الموظف المعني</label>
              <EmpPicker employees={employees} value={f.employee_id} onChange={(v) => setF((p) => ({ ...p, employee_id: v }))} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold text-slate-500">النوع</label>
                <select data-testid="decision-type" value={f.type} onChange={(e) => setF((p) => ({ ...p, type: e.target.value }))}
                  className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">التاريخ</label>
                <Input data-testid="decision-date" type="date" value={f.date} onChange={(e) => setF((p) => ({ ...p, date: e.target.value }))} className="h-9" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">عنوان القرار</label>
              <Input data-testid="decision-title" value={f.title} onChange={(e) => setF((p) => ({ ...p, title: e.target.value }))} placeholder="مثال: قرار تعيين" className="h-9" />
            </div>
            <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-2.5">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-bold text-blue-800"><Sparkles className="h-3.5 w-3.5" /> قرارات مقترحة (تعبئة تلقائية)</p>
              <div className="flex flex-wrap gap-1.5">
                {DECISION_TEMPLATES.map((tp) => (
                  <button key={tp.key} type="button" data-testid={`decision-tpl-${tp.key}`} disabled={locked} onClick={() => applyTpl(tp)}
                    className="rounded-full border border-blue-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-blue-700 transition hover:bg-blue-600 hover:text-white disabled:opacity-50">
                    {tp.title.replace("قرار ", "")}
                  </button>
                ))}
                {myTpls.map((tp) => (
                  <span key={tp.id} className={`inline-flex items-center gap-0.5 rounded-full border px-1 py-0.5 ${editTpl?.id === tp.id ? "border-amber-500 bg-amber-100" : "border-amber-300 bg-amber-50"}`} data-testid={`decision-mytpl-${tp.id}`}>
                    <button type="button" disabled={locked} onClick={() => applyTpl(tp)} data-testid={`decision-mytpl-apply-${tp.id}`}
                      className="rounded-full px-1.5 text-[11px] font-semibold text-amber-800 hover:text-amber-950 disabled:opacity-50">{tp.title}</button>
                    <button type="button" title="تعديل" disabled={locked} onClick={() => { applyTpl(tp); setEditTpl(tp); toast.info("عدّل النص ثم اضغط «حفظ كنموذج ثابت» واختر تحديث"); }} data-testid={`decision-mytpl-edit-${tp.id}`}
                      className="rounded-full p-0.5 text-amber-700 hover:bg-amber-200"><Pencil className="h-3 w-3" /></button>
                    <button type="button" title="حذف" onClick={() => delTpl(tp)} data-testid={`decision-mytpl-delete-${tp.id}`}
                      className="rounded-full p-0.5 text-red-600 hover:bg-red-100"><Trash2 className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
              <button type="button" disabled={locked} onClick={() => setTplOpen(true)} data-testid="decision-save-as-template"
                className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-amber-400 bg-white py-1.5 text-xs font-bold text-amber-800 hover:bg-amber-50 disabled:opacity-50">
                <BookmarkPlus className="h-3.5 w-3.5" /> {editTpl ? `حفظ / تحديث «${editTpl.title}»` : "حفظ كنموذج ثابت"}
              </button>
              <SaveDecisionTplDialog open={tplOpen} onOpenChange={setTplOpen} defaultTitle={f.title} editing={editTpl} onSave={saveTpl} />
            </div>
            <DecisionAIBox disabled={locked}
              getCtx={() => ({ html: ed.current?.getHTML() || "", type: f.type, title: f.title, date: f.date, employee_id: f.employee_id || null })}
              onResult={(r) => { fillInto(r.html, emp); if (r.title && !f.title) setF((p) => ({ ...p, title: r.title })); }} />
            <SignatorySelect testid="decision-signatory" value={f.signatory_id} onChange={(v) => setF((p) => ({ ...p, signatory_id: v }))} onPick={setSigner} />
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
              <span className="text-sm text-slate-700">يتطلب توقيع وبصمة الموظف <span className="text-xs text-slate-400">(اختياري)</span></span>
              <Switch data-testid="decision-require-sign" checked={f.require_signature} onCheckedChange={(v) => setF((p) => ({ ...p, require_signature: v }))} />
            </div>
            <div>
              <input ref={fileIn} type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
              {f.attachment_id ? (
                <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                  <Paperclip className="h-3.5 w-3.5" /><a href={fileUrl(f.attachment_id)} target="_blank" rel="noreferrer" className="flex-1 truncate underline">{f.attachment_name || "مرفق"}</a>
                  <button type="button" onClick={() => setF((p) => ({ ...p, attachment_id: null, attachment_name: null }))}><X className="h-3.5 w-3.5" /></button>
                </div>
              ) : (
                <Button type="button" variant="outline" data-testid="decision-attach" className="w-full gap-1.5" onClick={() => fileIn.current?.click()} disabled={busy === "upload"}>
                  {busy === "upload" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} إرفاق PDF
                </Button>
              )}
            </div>
            <div className="grid gap-2 pt-1">
              <Button data-testid="decision-save-send" disabled={!!busy || locked} onClick={() => save(true)} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700">
                {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} حفظ وإرسال للموظف
              </Button>
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="decision-save" variant="outline" disabled={!!busy || locked} onClick={() => save(false)} className="gap-1.5">
                  {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} حفظ مسودة
                </Button>
                <Button data-testid="decision-download-pdf" variant="outline" disabled={!!busy} onClick={download} className="gap-1.5">{busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} تنزيل PDF</Button>
              </div>
              <Button data-testid="decision-save-library" variant="outline" disabled={!!busy} onClick={toLibrary} className="h-auto gap-1.5 whitespace-normal border-blue-300 py-2 text-blue-800">{busy === "lib" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Library className="h-4 w-4" />} حفظ في مكتبة الشركة + نسخة لملف الموظف</Button>
              <button data-testid="decision-print" onClick={print} className="flex items-center justify-center gap-1 text-xs text-slate-500 hover:text-slate-800"><Printer className="h-3.5 w-3.5" /> طباعة</button>
              {user?.role === "manager" && <Button data-testid="decision-save-private" variant="outline" onClick={() => setPrivOpen(true)} className="gap-1.5 border-slate-900 text-slate-900"><Lock className="h-4 w-4" /> حفظ في الوثائق الخاصة</Button>}
              <SaveToPrivateDialog open={privOpen} onOpenChange={setPrivOpen} defaultName={docName} fileName={pdfName}
                makeBlob={() => decisionPDFBlob(docData(), sc, fileUrl)} />
            </div>
          </aside>
          <DocEditor ref={ed} initialHTML={decision?.body_html || ""}
            header={<>
              <Letterhead company={company} />
              <div className="mb-3 flex justify-between text-xs text-slate-500"><span>رقم القرار: <b>{decision?.number || "يُولَّد تلقائياً"}</b></span><span>التاريخ: <b>{f.date}</b></span></div>
              <h3 className="mb-4 text-center font-heading text-lg font-extrabold underline underline-offset-8">{f.title || "عنوان القرار"}</h3>
            </>}
            footer={<div className="mt-10 grid grid-cols-2 gap-6 text-center text-xs text-slate-500">
              <div><b className="text-slate-700">الموظف: {emp?.name || decision?.employee_name || "—"}</b><div className="mx-4 mt-10 border-b border-slate-300" />التوقيع والبصمة</div>
              <div><b className="text-slate-700">{sc?.official_title || "المدير العام"}</b>
                {sc?.signature_id ? <img src={fileUrl(sc.signature_id)} alt="" className="mx-auto h-14 object-contain" /> : <div className="mx-4 mt-10 border-b border-slate-300" />}الختم والتوقيع</div>
            </div>} />
        </div>
      </DialogContent>
    </Dialog>
  );
};
